import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useFormData, useFormActions } from '@hooks/useFormStore'
import { useOperatorAuth } from '@hooks/useOperatorAuth'
import { Button } from '@components/UI/Button'
import { Input } from '@components/UI/Input'
import { clsx } from 'clsx'
import type { FormData } from '@/types'
import styles from './FormSection.module.css'

type PagamentoFields = Pick<FormData,
  'iban' | 'bic_swift' | 'iban_intestato_a_me' | 'intestatario_iban' |
  'consenso_privacy' | 'consenso_marketing' | 'consenso_comunicazioni' |
  'modalita_invio_bolletta' | 'invio_indirizzo_tipo' | 'invio_indirizzo_altro' |
  'invio_cap_altro' | 'invio_citta_altro' | 'invio_provincia_altro' |
  'invio_email_tipo' | 'invio_email_altro'
> & {
  /** Opzionale finche' l'utente non seleziona un'opzione */
  metodo_pagamento?: 'pagoPA' | 'iban' | null
  /** Campi locali per il pagatore alternativo (mappati poi su intestatario_iban) */
  pagatore_nome?: string
  pagatore_cognome?: string
  pagatore_cf?: string
}

export function PagamentoSection() {
  const formData = useFormData()
  const { setData, nextStep, prevStep } = useFormActions()
  const { operator } = useOperatorAuth()

  // Agenzie Adventure: l'opzione "Bollettino PagoPA" e' nascosta sui soli
  // segmenti configurati (business), sul domestico resta selezionabile.
  const tipoCliente = formData.tipo_cliente === 'business' ? 'business' : 'domestico'
  const hiddenTipiBollettino = operator?.hide_bollettino_postale_tipi_cliente
    ?? (operator?.hide_bollettino_postale ? ['domestico', 'business'] : [])
  const hideBollettino = hiddenTipiBollettino.includes(tipoCliente)
  // Agenzie Adventure: e' consentito indicare un pagatore alternativo
  // (intestatario del conto diverso dal cliente).
  const allowPagatoreAlternativo = !!operator?.allow_pagatore_alternativo
  // Agenzie Adventure: e' consentito inserire un IBAN estero (non IT) + BIC/SWIFT.
  const foreignIbanEnabled = !!operator?.foreign_iban_enabled

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<PagamentoFields>({
    mode: 'onBlur',
    defaultValues: {
      metodo_pagamento: hideBollettino
        ? 'iban'
        : formData.metodo_pagamento === 'pagoPA' || formData.metodo_pagamento === 'iban'
          ? formData.metodo_pagamento
          : undefined,
      iban: formData.iban,
      bic_swift: formData.bic_swift,
      iban_intestato_a_me: formData.iban_intestato_a_me,
      intestatario_iban: formData.intestatario_iban,
      pagatore_nome: formData.intestatario_iban?.nome ?? '',
      pagatore_cognome: formData.intestatario_iban?.cognome ?? '',
      pagatore_cf: formData.intestatario_iban?.codice_fiscale ?? '',
      consenso_privacy: formData.consenso_privacy,
      consenso_marketing: formData.consenso_marketing,
      consenso_comunicazioni: formData.consenso_comunicazioni,
      // Modalita invio bolletta
      modalita_invio_bolletta: formData.modalita_invio_bolletta,
      invio_indirizzo_tipo: formData.invio_indirizzo_tipo,
      invio_indirizzo_altro: formData.invio_indirizzo_altro,
      invio_cap_altro: formData.invio_cap_altro,
      invio_citta_altro: formData.invio_citta_altro,
      invio_provincia_altro: formData.invio_provincia_altro,
      invio_email_tipo: formData.invio_email_tipo,
      invio_email_altro: formData.invio_email_altro
    }
  })

  const metodoPagamento = watch('metodo_pagamento')
  const ibanIntestatoAMe = watch('iban_intestato_a_me')
  const modalitaInvio = watch('modalita_invio_bolletta')
  const invioIndirizzoTipo = watch('invio_indirizzo_tipo')
  const invioEmailTipo = watch('invio_email_tipo')
  const ibanValue = watch('iban')
  // IBAN estero = valorizzato e non inizia per IT. Solo rilevante se abilitato.
  const isForeignIban = foreignIbanEnabled &&
    !!ibanValue && !(ibanValue || '').toUpperCase().trim().startsWith('IT')

  // Il flag operatore arriva in modo asincrono (fetch /operator/me al mount):
  // quando risulta attivo, forziamo la selezione su IBAN se era su pagoPA/vuoto.
  useEffect(() => {
    if (hideBollettino && metodoPagamento !== 'iban') {
      setValue('metodo_pagamento', 'iban')
    }
  }, [hideBollettino, metodoPagamento, setValue])

  const onSubmit = (formValues: PagamentoFields) => {
    const metodo = formValues.metodo_pagamento
    if (metodo !== 'pagoPA' && metodo !== 'iban') {
      return
    }

    // Estrai i campi locali del pagatore alternativo: non fanno parte di FormData
    // e vengono mappati su `intestatario_iban`.
    const { pagatore_nome, pagatore_cognome, pagatore_cf, ...data } = formValues

    if (metodo === 'iban') {
      if (allowPagatoreAlternativo && data.iban_intestato_a_me === false) {
        // Pagatore alternativo: l'intestatario del conto e' diverso dal cliente.
        data.iban_intestato_a_me = false
        data.intestatario_iban = {
          nome: (pagatore_nome ?? '').trim(),
          cognome: (pagatore_cognome ?? '').trim(),
          codice_fiscale: (pagatore_cf ?? '').toUpperCase().trim()
        }
      } else {
        // Comportamento standard: intestatario = cliente.
        data.iban_intestato_a_me = true
        data.intestatario_iban = {
          nome: formData.nome,
          cognome: formData.cognome,
          codice_fiscale: formData.codice_fiscale
        }
      }
      // BIC/SWIFT: si conserva solo per IBAN estero (non IT); altrimenti si azzera.
      const ibanUp = (data.iban || '').toUpperCase().trim()
      const foreign = foreignIbanEnabled && !!ibanUp && !ibanUp.startsWith('IT')
      data.bic_swift = foreign ? (data.bic_swift || '').toUpperCase().trim() : ''
    } else {
      data.iban = ''
      data.bic_swift = ''
      data.iban_intestato_a_me = true
      data.intestatario_iban = null
    }

    setData({
      ...data,
      metodo_pagamento: metodo
    })
    nextStep()
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <i className="bi bi-credit-card-fill" />
        </div>
        <div>
          <h2 className={styles.sectionTitle}>Metodo di Pagamento</h2>
          <p className={styles.sectionDescription}>
            {hideBollettino
              ? 'Il pagamento avviene tramite addebito su conto (IBAN)'
              : 'Scegli come preferisci pagare le bollette (obbligatorio)'}
          </p>
        </div>
      </div>

      {errors.metodo_pagamento && (
        <div className={styles.attachmentError} role="alert">
          {errors.metodo_pagamento.message}
        </div>
      )}

      {/* Metodo pagamento selector */}
      <div className={styles.paymentOptions}>
        {!hideBollettino && (
          <label
            className={clsx(styles.paymentOption, {
              [styles.paymentActive]: metodoPagamento === 'pagoPA'
            })}
          >
            <input
              type="radio"
              value="pagoPA"
              {...register('metodo_pagamento', {
                required: 'Seleziona bollettino PagoPA oppure addebito su conto (IBAN)'
              })}
              className={styles.paymentRadio}
            />
            <div className={styles.paymentIcon}>
              <i className="bi bi-file-earmark-text" />
            </div>
            <div className={styles.paymentContent}>
              <span className={styles.paymentTitle}>Bollettino PagoPA</span>
              <span className={styles.paymentDescription}>
                Ricevi il bollettino e paga quando vuoi
              </span>
            </div>
            <div className={styles.paymentCheck}>
              <i className="bi bi-check-lg" />
            </div>
          </label>
        )}

        <label
          className={clsx(styles.paymentOption, {
            [styles.paymentActive]: metodoPagamento === 'iban'
          })}
        >
          <input
            type="radio"
            value="iban"
            {...register('metodo_pagamento', {
              required: hideBollettino ? 'Seleziona addebito su conto (IBAN)' : false
            })}
            className={styles.paymentRadio}
          />
          <div className={styles.paymentIcon}>
            <i className="bi bi-bank" />
          </div>
          <div className={styles.paymentContent}>
            <span className={styles.paymentTitle}>Addebito su conto</span>
            <span className={styles.paymentDescription}>
              Pagamento automatico tramite SEPA
            </span>
          </div>
          <div className={styles.paymentCheck}>
            <i className="bi bi-check-lg" />
          </div>
        </label>
      </div>

      {/* ============================================
          MODALITA INVIO BOLLETTA
          ============================================ */}
      <div className={styles.divider}>
        <span>Modalita Invio Bolletta</span>
      </div>

      <div className={styles.modalitaInvioSection}>
        <div className={styles.modalitaInvioHeader}>
          <i className="bi bi-send" />
          <span>Come vuoi ricevere la bolletta?</span>
        </div>

        <div className={styles.modalitaOptions}>
          {/* Opzione Email */}
          <div
            className={clsx(styles.modalitaOption, {
              [styles.modalitaActive]: modalitaInvio === 'email'
            })}
            onClick={() => setValue('modalita_invio_bolletta', 'email')}
          >
            <div className={styles.modalitaIcon}>
              <i className="bi bi-envelope-at" />
            </div>
            <div className={styles.modalitaContent}>
              <span className={styles.modalitaLabel}>Via Email</span>
              <span className={styles.modalitaDesc}>Ricevi la bolletta digitale nella tua casella email</span>
            </div>
            <div className={styles.modalitaCheck}>
              <i className="bi bi-check-lg" />
            </div>
          </div>

          {/* Sub-options per Email */}
          {modalitaInvio === 'email' && (
            <div className={styles.subOptions}>
              <span className={styles.subOptionLabel}>Invia all'indirizzo email:</span>
              <div className={styles.subOptionsGrid}>
                <label
                  className={clsx(styles.subOptionItem, {
                    [styles.subOptionActive]: invioEmailTipo === 'anagrafica'
                  })}
                >
                  <input
                    type="radio"
                    value="anagrafica"
                    {...register('invio_email_tipo')}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioCustom} />
                  <span className={styles.subOptionText}>
                    Email anagrafica: <strong>{formData.email}</strong>
                  </span>
                </label>
                
                <label
                  className={clsx(styles.subOptionItem, {
                    [styles.subOptionActive]: invioEmailTipo === 'altro'
                  })}
                >
                  <input
                    type="radio"
                    value="altro"
                    {...register('invio_email_tipo')}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioCustom} />
                  <span className={styles.subOptionText}>Altro indirizzo email</span>
                </label>
              </div>

              {/* Campo email alternativo */}
              {invioEmailTipo === 'altro' && (
                <div className={styles.indirizzoAltroFields}>
                  <Input
                    label="Indirizzo Email"
                    icon="envelope"
                    type="email"
                    required
                    placeholder="bolletta@esempio.it"
                    error={errors.invio_email_altro?.message}
                    {...register('invio_email_altro', {
                      required: invioEmailTipo === 'altro' ? 'Email obbligatoria' : false,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Email non valida'
                      }
                    })}
                  />
                </div>
              )}
            </div>
          )}

          {/* Opzione Posta */}
          <div
            className={clsx(styles.modalitaOption, {
              [styles.modalitaActive]: modalitaInvio === 'posta'
            })}
            onClick={() => setValue('modalita_invio_bolletta', 'posta')}
          >
            <div className={styles.modalitaIcon}>
              <i className="bi bi-mailbox" />
            </div>
            <div className={styles.modalitaContent}>
              <span className={styles.modalitaLabel}>Posta Ordinaria</span>
              <span className={styles.modalitaDesc}>Ricevi la bolletta cartacea al tuo indirizzo</span>
            </div>
            <div className={styles.modalitaCheck}>
              <i className="bi bi-check-lg" />
            </div>
          </div>

          {/* Sub-options per Posta */}
          {modalitaInvio === 'posta' && (
            <div className={styles.subOptions}>
              <span className={styles.subOptionLabel}>Invia all'indirizzo:</span>
              <div className={styles.subOptionsGrid}>
                <label
                  className={clsx(styles.subOptionItem, {
                    [styles.subOptionActive]: invioIndirizzoTipo === 'residenza'
                  })}
                >
                  <input
                    type="radio"
                    value="residenza"
                    {...register('invio_indirizzo_tipo')}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioCustom} />
                  <span className={styles.subOptionText}>
                    Residenza: {formData.indirizzo_residenza}, {formData.cap_residenza} {formData.citta_residenza}
                  </span>
                </label>
                
                <label
                  className={clsx(styles.subOptionItem, {
                    [styles.subOptionActive]: invioIndirizzoTipo === 'fornitura'
                  })}
                >
                  <input
                    type="radio"
                    value="fornitura"
                    {...register('invio_indirizzo_tipo')}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioCustom} />
                  <span className={styles.subOptionText}>
                    Fornitura: {formData.stesso_indirizzo 
                      ? `${formData.indirizzo_residenza}, ${formData.cap_residenza} ${formData.citta_residenza}`
                      : `${formData.indirizzo_fornitura}, ${formData.cap_fornitura} ${formData.citta_fornitura}`
                    }
                  </span>
                </label>
                
                <label
                  className={clsx(styles.subOptionItem, {
                    [styles.subOptionActive]: invioIndirizzoTipo === 'altro'
                  })}
                >
                  <input
                    type="radio"
                    value="altro"
                    {...register('invio_indirizzo_tipo')}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioCustom} />
                  <span className={styles.subOptionText}>Altro indirizzo</span>
                </label>
              </div>

              {/* Campi indirizzo alternativo */}
              {invioIndirizzoTipo === 'altro' && (
                <div className={styles.indirizzoAltroFields}>
                  <div className={styles.formGrid}>
                    <div className={styles.fullWidth}>
                      <Input
                        label="Indirizzo"
                        icon="geo-alt"
                        required
                        placeholder="Via Roma, 1"
                        error={errors.invio_indirizzo_altro?.message}
                        {...register('invio_indirizzo_altro', {
                          required: invioIndirizzoTipo === 'altro' ? 'Indirizzo obbligatorio' : false
                        })}
                      />
                    </div>
                    <Input
                      label="CAP"
                      icon="mailbox2"
                      required
                      placeholder="00100"
                      maxLength={5}
                      error={errors.invio_cap_altro?.message}
                      {...register('invio_cap_altro', {
                        required: invioIndirizzoTipo === 'altro' ? 'CAP obbligatorio' : false,
                        pattern: {
                          value: /^[0-9]{5}$/,
                          message: 'CAP deve essere di 5 cifre'
                        }
                      })}
                    />
                    <Input
                      label="Citta"
                      icon="building"
                      required
                      placeholder="Roma"
                      error={errors.invio_citta_altro?.message}
                      {...register('invio_citta_altro', {
                        required: invioIndirizzoTipo === 'altro' ? 'Citta obbligatoria' : false
                      })}
                    />
                    <Input
                      label="Provincia"
                      icon="map"
                      required
                      uppercase
                      placeholder="RM"
                      maxLength={2}
                      error={errors.invio_provincia_altro?.message}
                      {...register('invio_provincia_altro', {
                        required: invioIndirizzoTipo === 'altro' ? 'Provincia obbligatoria' : false,
                        pattern: {
                          value: /^[A-Z]{2}$/i,
                          message: 'Inserisci la sigla provincia (es: RM)'
                        }
                      })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* IBAN section */}
      {metodoPagamento === 'iban' && (
        <div className={styles.ibanSection}>
          <Input
            label="IBAN"
            icon="bank2"
            required
            uppercase
            placeholder={foreignIbanEnabled ? 'IT.. oppure IBAN estero (es. DE..)' : 'IT60X0542811101000000123456'}
            hint={foreignIbanEnabled
              ? "IBAN italiano (IT, 27 caratteri) o estero (con BIC/SWIFT)"
              : "L'IBAN italiano inizia con IT e ha 27 caratteri"}
            error={errors.iban?.message}
            {...register('iban', {
              required: metodoPagamento === 'iban' ? 'IBAN obbligatorio' : false,
              pattern: foreignIbanEnabled
                ? {
                    // IBAN generico: 2 lettere paese + 2 cifre controllo + BBAN (11-30 alfanumerici)
                    value: /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/i,
                    message: 'IBAN non valido'
                  }
                : {
                    value: /^IT[0-9]{2}[A-Z][0-9]{10}[A-Z0-9]{12}$/i,
                    message: 'IBAN italiano non valido'
                  }
            })}
          />

          {/* BIC/SWIFT: solo agenzie abilitate; obbligatorio con IBAN estero (non IT) */}
          {foreignIbanEnabled && (
            <Input
              label="BIC / SWIFT"
              icon="bank"
              uppercase
              required={isForeignIban}
              placeholder="DEUTDEFF oppure DEUTDEFF500"
              hint={isForeignIban
                ? 'Obbligatorio per IBAN estero (8 o 11 caratteri)'
                : 'Richiesto solo per IBAN estero (non IT)'}
              error={errors.bic_swift?.message}
              {...register('bic_swift', {
                validate: (value) => {
                  if (!isForeignIban) return true
                  const v = (value || '').toUpperCase().trim()
                  if (!v) return 'BIC/SWIFT obbligatorio per IBAN estero'
                  return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(v) || 'BIC/SWIFT non valido (8 o 11 caratteri)'
                }
              })}
            />
          )}
          
          {/* Intestatario del conto */}
          <div className={styles.intestatarioSection}>
            <div className={styles.intestatarioTitle}>
              <i className="bi bi-person-vcard" />
              <span>Intestatario del conto</span>
            </div>

            {allowPagatoreAlternativo ? (
              <>
                <div className={styles.intestatarioOptions}>
                  <div
                    className={clsx(styles.intestatarioButton, {
                      [styles.intestatarioActive]: ibanIntestatoAMe !== false
                    })}
                    onClick={() => setValue('iban_intestato_a_me', true)}
                  >
                    <div className={styles.intestatarioIcon}>
                      <i className="bi bi-person-check" />
                    </div>
                    <div className={styles.intestatarioContent}>
                      <span className={styles.intestatarioLabel}>Intestato a me</span>
                      <span className={styles.intestatarioDesc}>
                        {formData.nome} {formData.cognome}
                      </span>
                    </div>
                    <div className={styles.intestatarioCheck}>
                      <i className="bi bi-check-lg" />
                    </div>
                  </div>

                  <div
                    className={clsx(styles.intestatarioButton, {
                      [styles.intestatarioActive]: ibanIntestatoAMe === false
                    })}
                    onClick={() => setValue('iban_intestato_a_me', false)}
                  >
                    <div className={styles.intestatarioIcon}>
                      <i className="bi bi-people" />
                    </div>
                    <div className={styles.intestatarioContent}>
                      <span className={styles.intestatarioLabel}>Intestatario diverso</span>
                      <span className={styles.intestatarioDesc}>
                        Il conto è intestato a un'altra persona
                      </span>
                    </div>
                    <div className={styles.intestatarioCheck}>
                      <i className="bi bi-check-lg" />
                    </div>
                  </div>
                </div>

                {ibanIntestatoAMe === false && (
                  <div className={styles.indirizzoAltroFields}>
                    <div className={styles.formGrid}>
                      <Input
                        label="Nome intestatario"
                        icon="person"
                        required
                        placeholder="Mario"
                        error={errors.pagatore_nome?.message}
                        {...register('pagatore_nome', {
                          required: ibanIntestatoAMe === false ? 'Nome intestatario obbligatorio' : false
                        })}
                      />
                      <Input
                        label="Cognome intestatario"
                        icon="person"
                        required
                        placeholder="Rossi"
                        error={errors.pagatore_cognome?.message}
                        {...register('pagatore_cognome', {
                          required: ibanIntestatoAMe === false ? 'Cognome intestatario obbligatorio' : false
                        })}
                      />
                      <div className={styles.fullWidth}>
                        <Input
                          label="Codice Fiscale intestatario"
                          icon="person-vcard"
                          required
                          uppercase
                          maxLength={16}
                          placeholder="RSSMRA80A01H501U"
                          error={errors.pagatore_cf?.message}
                          {...register('pagatore_cf', {
                            required: ibanIntestatoAMe === false ? 'Codice Fiscale intestatario obbligatorio' : false,
                            pattern: {
                              value: /^[A-Z0-9]{16}$/i,
                              message: 'Codice Fiscale non valido (16 caratteri)'
                            }
                          })}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.intestatarioOptions}>
                <div className={clsx(styles.intestatarioButton, styles.intestatarioActive)}>
                  <div className={styles.intestatarioIcon}>
                    <i className="bi bi-person-check" />
                  </div>
                  <div className={styles.intestatarioContent}>
                    <span className={styles.intestatarioLabel}>Intestato a me</span>
                    <span className={styles.intestatarioDesc}>
                      {formData.nome} {formData.cognome}
                    </span>
                  </div>
                  <div className={styles.intestatarioCheck}>
                    <i className="bi bi-check-lg" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Consensi */}
      <div className={styles.divider}>
        <span>Consensi e Privacy</span>
      </div>

      <div className={styles.consents}>
        <label className={styles.consent}>
          <input
            type="checkbox"
            {...register('consenso_privacy', {
              required: 'Consenso privacy obbligatorio'
            })}
            className={styles.checkbox}
          />
          <span className={styles.checkmark}>
            <i className="bi bi-check-lg" />
          </span>
          <span className={styles.consentText}>
            <strong>Accetto l'informativa sulla privacy</strong> e il trattamento dei dati personali ai sensi del GDPR *
            {errors.consenso_privacy && (
              <span className={styles.consentError}>{errors.consenso_privacy.message}</span>
            )}
          </span>
        </label>

        <label className={styles.consent}>
          <input
            type="checkbox"
            {...register('consenso_marketing')}
            className={styles.checkbox}
          />
          <span className={styles.checkmark}>
            <i className="bi bi-check-lg" />
          </span>
          <span className={styles.consentText}>
            Il Cliente dichiara di avere preso visione dell'informativa redatta ai sensi dell'art. 13 del Regolamento UE 2016/679 e conferisce/nega il consenso alle seguenti attività: • effettuazione di contatti telefonici e comunicazioni elettroniche per l'invio di materiale pubblicitario o di vendita diretta o per il compimento di ricerche di mercato o di comunicazione commerciale o, comunque, per finalità promozionali (lettera e. delle "Finalità").
          </span>
        </label>

        <label className={styles.consent}>
          <input
            type="checkbox"
            {...register('consenso_comunicazioni')}
            className={styles.checkbox}
          />
          <span className={styles.checkmark}>
            <i className="bi bi-check-lg" />
          </span>
          <span className={styles.consentText}>
            Il Cliente dichiara di avere preso visione dell'informativa redatta ai sensi dell'art. 13 del Regolamento UE 2016/679 e conferisce/nega il consenso alle seguenti attività: • svolgimento di attività volte all'analisi delle preferenze, abitudini e/o scelte di consumo dell'interessato, dallo stesso manifestate anche attraverso l'utilizzo delle digital properties del Titolare, al fine di offrire prodotti e servizi in linea con il profilo dell'interessato stesso (lettera f. delle "Finalità").
          </span>
        </label>
      </div>

      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          icon="arrow-left"
          onClick={prevStep}
        >
          Indietro
        </Button>
        
        <Button
          type="submit"
          icon="arrow-right"
          iconPosition="right"
        >
          Continua
        </Button>
      </div>
    </form>
  )
}
